import httpService from '../httpService';
import { store } from '../../redux/store';
import Sponsor, {
  SponsorCampaignType,
  SponsorDetail,
  SponsorLinkableEntities,
  SponsorLinksPayload,
  SponsorUpdatePayload,
} from '../../types/sponsor';

/**
 * Manages the sponsors of the currently selected business.
 */
const sponsorService = {
  /**
   * Gets the sponsors of the selected business, ordered by their display order.
   *
   * @returns {Promise<Sponsor[]>} A Promise that resolves with the sponsors.
   */
  getSponsors: async (): Promise<Sponsor[]> => {
    const businessId = store.getState().application.selectedBusiness;
    const { data } = await httpService.get<Sponsor[]>(`/networks/businesses/${businessId}/sponsors`);
    return data;
  },

  /**
   * Gets a single sponsor including placements, stats and entity links.
   */
  getSponsor: async (sponsorId: number): Promise<SponsorDetail> => {
    const businessId = store.getState().application.selectedBusiness;
    const { data } = await httpService.get<SponsorDetail>(
      `/networks/businesses/${businessId}/sponsors/${sponsorId}`,
    );
    return data;
  },

  /**
   * Gets entities across the business' communities that can be linked to a sponsor.
   */
  getLinkableEntities: async (): Promise<SponsorLinkableEntities> => {
    const businessId = store.getState().application.selectedBusiness;
    const { data } = await httpService.get<SponsorLinkableEntities>(
      `/networks/businesses/${businessId}/sponsors/linkable-entities`,
    );
    return data;
  },

  /**
   * Creates a new sponsor campaign for the selected business.
   */
  createSponsor: async (
    name: string,
    url?: string,
    logo?: File,
    description?: string,
    campaignType?: SponsorCampaignType,
  ): Promise<Sponsor> => {
    const businessId = store.getState().application.selectedBusiness;
    const formData = new FormData();
    formData.append('name', name);
    if (url) formData.append('url', url);
    if (description) formData.append('description', description);
    if (campaignType) formData.append('campaignType', campaignType);
    if (logo) formData.append('logo', logo);

    const { data } = await httpService.post<Sponsor>(
      `/networks/businesses/${businessId}/sponsors`,
      formData,
    );
    return data;
  },

  /**
   * Updates a sponsor of the selected business.
   */
  updateSponsor: async (sponsorId: number, updates: SponsorUpdatePayload): Promise<Sponsor> => {
    const businessId = store.getState().application.selectedBusiness;
    const formData = new FormData();
    if (updates.name !== undefined) formData.append('name', updates.name);
    if (updates.url !== undefined) formData.append('url', updates.url);
    if (updates.description !== undefined) formData.append('description', updates.description);
    if (updates.logo) formData.append('logo', updates.logo);
    if (updates.active !== undefined) formData.append('active', String(updates.active));
    if (updates.showOnHome !== undefined) formData.append('showOnHome', String(updates.showOnHome));
    if (updates.showOnEvents !== undefined) {
      formData.append('showOnEvents', String(updates.showOnEvents));
    }
    if (updates.showOnPhotoAlbums !== undefined) {
      formData.append('showOnPhotoAlbums', String(updates.showOnPhotoAlbums));
    }
    if (updates.showOnScoreCards !== undefined) {
      formData.append('showOnScoreCards', String(updates.showOnScoreCards));
    }
    if (updates.showOnTasks !== undefined) formData.append('showOnTasks', String(updates.showOnTasks));

    const { data } = await httpService.put<Sponsor>(
      `/networks/businesses/${businessId}/sponsors/${sponsorId}`,
      formData,
    );
    return data;
  },

  /**
   * Replaces the entity links of a sponsor.
   */
  updateSponsorLinks: async (
    sponsorId: number,
    links: SponsorLinksPayload,
  ): Promise<SponsorDetail> => {
    const businessId = store.getState().application.selectedBusiness;
    const { data } = await httpService.put<SponsorDetail>(
      `/networks/businesses/${businessId}/sponsors/${sponsorId}/links`,
      links,
    );
    return data;
  },

  /**
   * Deletes a sponsor of the selected business.
   */
  deleteSponsor: async (sponsorId: number): Promise<void> => {
    const businessId = store.getState().application.selectedBusiness;
    await httpService.delete(`/networks/businesses/${businessId}/sponsors/${sponsorId}`);
  },
};

export default sponsorService;
